"use client";

import React, { useCallback, useEffect, useState } from "react";
import Image, { type ImageProps } from "next/image";
import {
  ChevronLeft,
  ChevronRight,
  X,
  LayoutGrid,
  GalleryHorizontalEnd,
  ZoomIn,
  ImageIcon,
} from "lucide-react";

/**
 * next/image with a proper loading state: a pulsing placeholder until the
 * photo arrives, then a fade-in. If the optimizer fails (e.g. the server can't
 * fetch the tenant's storage host) it retries the original URL unoptimized,
 * and only if that fails too shows a neutral placeholder — never a broken
 * image or raw alt text.
 */
function GalleryImg({ className = "", ...props }: ImageProps) {
  const [state, setState] = useState<"loading" | "loaded" | "fallback" | "failed">("loading");
  if (state === "failed") {
    return (
      <span className="absolute inset-0 flex items-center justify-center bg-muted text-muted-foreground/50" aria-hidden>
        <ImageIcon className="h-8 w-8" />
      </span>
    );
  }
  return (
    <>
      {state !== "loaded" && (
        <span className="absolute inset-0 flex animate-pulse items-center justify-center bg-muted text-muted-foreground/40" aria-hidden>
          <ImageIcon className="h-7 w-7" />
        </span>
      )}
      <Image
        {...props}
        key={state === "fallback" ? "fallback" : "optimized"}
        unoptimized={state === "fallback" || props.unoptimized}
        onLoad={() => setState("loaded")}
        onError={() => setState((s) => (s === "loading" ? "fallback" : "failed"))}
        className={`${className} transition-opacity duration-500 ${state === "loaded" ? "opacity-100" : "opacity-0"}`}
      />
    </>
  );
}

interface GalleryImage {
  mediaId?: string;
  url: string;
  alt?: { en?: string; mm?: string };
  caption?: { en?: string; mm?: string };
}

interface GalleryBodyProps {
  images?: GalleryImage[];
  currentLanguage?: "en" | "mm";
}

/**
 * Gallery body — two view modes, both themed off the site's CSS color tokens
 * (`primary` / `border` / `card` / `muted-foreground`, so it tracks whatever
 * palette the active theme ships):
 *
 *   • List  — responsive masonry-ish grid; click any tile to open the lightbox.
 *   • Slide — best-practice carousel: large stage + prev/next arrows + a
 *             thumbnail filmstrip; click the stage to open the lightbox.
 *
 * The lightbox is a fullscreen overlay with prev/next + keyboard (←/→/Esc) and
 * a body-scroll lock. Captions (per `currentLanguage`, falling back to `en`)
 * render under tiles, under the slide stage, and in the lightbox when present.
 */
export function GalleryBody({
  images,
  currentLanguage = "en",
}: GalleryBodyProps) {
  const items = (images ?? []).filter((i) => i && i.url);
  const [view, setView] = useState<"list" | "slide">("list");
  const [current, setCurrent] = useState(0);
  const [lightbox, setLightbox] = useState<number | null>(null);

  const altOf = useCallback(
    (img: GalleryImage, i: number) =>
      img.alt?.[currentLanguage] || img.alt?.en || `Image ${i + 1}`,
    [currentLanguage],
  );
  const capOf = useCallback(
    (img: GalleryImage) =>
      img.caption?.[currentLanguage] || img.caption?.en || null,
    [currentLanguage],
  );

  const count = items.length;
  const go = useCallback(
    (delta: number, setter: (n: number) => void, base: number) => {
      if (count === 0) return;
      setter((base + delta + count) % count);
    },
    [count],
  );

  // Lightbox keyboard nav + body-scroll lock.
  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightbox(null);
      else if (e.key === "ArrowRight")
        setLightbox((n) => (n === null ? n : (n + 1) % count));
      else if (e.key === "ArrowLeft")
        setLightbox((n) => (n === null ? n : (n - 1 + count) % count));
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [lightbox, count]);

  if (count === 0) return null;

  return (
    <div data-post-body="gallery" className="not-prose">
      {/* View switch */}
      <div className="mb-4 flex items-center justify-end gap-1">
        <ViewToggle
          active={view === "list"}
          onClick={() => setView("list")}
          label="Grid"
        >
          <LayoutGrid className="h-4 w-4" />
        </ViewToggle>
        <ViewToggle
          active={view === "slide"}
          onClick={() => setView("slide")}
          label="Slideshow"
        >
          <GalleryHorizontalEnd className="h-4 w-4" />
        </ViewToggle>
      </div>

      {view === "list" ? (
        /* ── List / grid view ── */
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {items.map((img, i) => {
            const caption = capOf(img);
            return (
              <figure
                key={img.mediaId ?? `${img.url}-${i}`}
                className="group overflow-hidden rounded-lg border border-border bg-card"
              >
                <button
                  type="button"
                  onClick={() => setLightbox(i)}
                  aria-label={`Open ${altOf(img, i)}`}
                  className="relative block aspect-[4/3] w-full cursor-zoom-in bg-muted"
                >
                  <GalleryImg
                    src={img.url}
                    alt={altOf(img, i)}
                    fill
                    sizes="(max-width:640px) 50vw, (max-width:1024px) 33vw, 25vw"
                    className="object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition group-hover:bg-black/25 group-hover:opacity-100">
                    <ZoomIn className="h-6 w-6 text-white drop-shadow" />
                  </span>
                </button>
                {caption && (
                  <figcaption className="truncate border-t border-border px-3 py-2 text-xs text-muted-foreground">
                    {caption}
                  </figcaption>
                )}
              </figure>
            );
          })}
        </div>
      ) : (
        /* ── Slide / carousel view ── */
        <Slideshow
          items={items}
          current={current}
          setCurrent={setCurrent}
          altOf={altOf}
          capOf={capOf}
          onZoom={() => setLightbox(current)}
          go={go}
        />
      )}

      {/* ── Lightbox overlay ── */}
      {lightbox !== null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={altOf(items[lightbox], lightbox)}
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[60] flex flex-col items-center justify-center bg-black/90 p-4"
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            aria-label="Close"
            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-2xl leading-none text-white transition-colors hover:bg-white/20"
          >
            <X className="h-5 w-5" />
          </button>

          {count > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightbox((n) => (n === null ? n : (n - 1 + count) % count));
                }}
                aria-label="Previous"
                className="absolute left-2 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 sm:left-4"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightbox((n) => (n === null ? n : (n + 1) % count));
                }}
                aria-label="Next"
                className="absolute right-2 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 sm:right-4"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}

          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={items[lightbox].url}
            alt={altOf(items[lightbox], lightbox)}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[82vh] max-w-[92vw] cursor-default rounded object-contain shadow-2xl"
          />
          {capOf(items[lightbox]) && (
            <p className="mt-4 max-w-[80vw] text-center text-sm text-white/90">
              {capOf(items[lightbox])}
            </p>
          )}
          {count > 1 && (
            <p className="mt-2 text-xs text-white/60">
              {lightbox + 1} / {count}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** Themed segmented-control button for the view switch. */
function ViewToggle({
  active,
  onClick,
  label,
  children,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active ? "true" : "false"}
      title={label}
      className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-card text-muted-foreground hover:bg-muted"
      }`}
    >
      {children}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

/** Carousel: large stage + prev/next + thumbnail filmstrip. */
function Slideshow({
  items,
  current,
  setCurrent,
  altOf,
  capOf,
  onZoom,
  go,
}: {
  items: GalleryImage[];
  current: number;
  setCurrent: (n: number) => void;
  altOf: (img: GalleryImage, i: number) => string;
  capOf: (img: GalleryImage) => string | null;
  onZoom: () => void;
  go: (delta: number, setter: (n: number) => void, base: number) => void;
}) {
  const count = items.length;
  const active = items[current] ?? items[0];
  const caption = capOf(active);

  return (
    <div className="space-y-3">
      {/* Stage */}
      <div className="relative overflow-hidden rounded-lg border border-border bg-muted">
        <button
          type="button"
          onClick={onZoom}
          aria-label={`Zoom ${altOf(active, current)}`}
          className="relative block aspect-video w-full cursor-zoom-in"
        >
          <GalleryImg
            key={active.url}
            src={active.url}
            alt={altOf(active, current)}
            fill
            sizes="(max-width:1024px) 100vw, 66vw"
            className="object-contain"
            priority
          />
        </button>

        {count > 1 && (
          <>
            <button
              type="button"
              onClick={() => go(-1, setCurrent, current)}
              aria-label="Previous image"
              className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background/70 text-foreground shadow backdrop-blur transition-colors hover:bg-background"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => go(1, setCurrent, current)}
              aria-label="Next image"
              className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background/70 text-foreground shadow backdrop-blur transition-colors hover:bg-background"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            <span className="absolute bottom-2 right-3 rounded-full bg-black/55 px-2 py-0.5 text-xs text-white tabular-nums">
              {current + 1} / {count}
            </span>
          </>
        )}
      </div>

      {caption && (
        <p className="text-center text-sm text-muted-foreground">{caption}</p>
      )}

      {/* Thumbnail filmstrip */}
      {count > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {items.map((img, i) => (
            <button
              key={img.mediaId ?? `${img.url}-thumb-${i}`}
              type="button"
              onClick={() => setCurrent(i)}
              aria-label={`Show ${altOf(img, i)}`}
              aria-current={i === current}
              className={`relative h-16 w-20 flex-shrink-0 overflow-hidden rounded-md border-2 bg-muted transition ${
                i === current
                  ? "border-primary opacity-100"
                  : "border-transparent opacity-60 hover:opacity-100"
              }`}
            >
              <GalleryImg
                src={img.url}
                alt={altOf(img, i)}
                fill
                sizes="80px"
                className="object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default GalleryBody;
